import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface UserSettings {
  theme_mode: 'dark' | 'light';
  anonymizing_mode: boolean;
}

export function useUserSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    try {
      // First try to get existing settings
      const { data: existingSettings, error: fetchError } = await supabase
        .from('nrm_user_settings')
        .select('*')
        .eq('user_id', user?.id)
        .select()
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (!existingSettings) {
        // Create default settings if none exist
        const { data: newSettings, error: insertError } = await supabase
          .from('nrm_user_settings')
          .insert({
            user_id: user?.id,
            theme_mode: 'dark',
            anonymizing_mode: false
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setSettings(newSettings);
      } else {
        setSettings(existingSettings);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<UserSettings>) => {
    try {
      if (!user?.id) throw new Error('No user found');

      const { data: updatedSettings, error: updateError } = await supabase
        .from('nrm_user_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id)
        .select()
        .single();

      if (updateError) throw updateError;
      if (!updatedSettings) throw new Error('Failed to get updated settings');
      
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Failed to update settings:', err);
      setError(err.message || 'Failed to update settings');
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings
  };
}